import React, { useEffect, useState } from "react";
import { CheckCircle, Copy } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls =
  "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const ROLES            = ["staff", "manager", "admin", "executive", "superadmin"];
const BANDS            = ["", "Junior", "Mid", "Senior", "Lead"];
const EMPLOYMENT_TYPES = ["", "Full-time", "Part-time", "Contract"];

/**
 * Single-user invite form. POSTs /api/admin/users/invite. The backend
 * also writes the optional profile prefill fields (band/start_date/
 * manager_id/work_email/work_phone/employment_type) onto the new
 * StaffProfile so the user lands with a partially-filled profile.
 */
export default function CreateUserForm({ onCreated }) {
  const [form, setForm] = useState({
    email:           "",
    full_name:       "",
    role:            "staff",
    department:      "",
    designation:     "",
    band:            "",
    start_date:      "",
    manager_id:      "",
    work_email:      "",
    work_phone:      "",
    employment_type: "",
  });
  const [managers,   setManagers]   = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [success,    setSuccess]    = useState(null);
  const [copied,     setCopied]     = useState(false);
  const token = localStorage.getItem("token");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load the list of managers in the org so the admin can pick one as
  // this user's reporting line. Best-effort — if the endpoint fails the
  // dropdown is left empty and the field stays optional.
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/users?role=manager`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setManagers(Array.isArray(rows) ? rows : []))
      .catch(() => setManagers([]));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null); setSuccess(null); setCopied(false);
    if (!form.email.trim() || !form.full_name.trim()) {
      setError("Email and full name are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Strip empty optional fields so the backend keeps defaults.
      const body = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      const res = await fetch(`${API_BASE}/api/admin/users/invite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const respBody = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = respBody?.detail || respBody?.message || `HTTP ${res.status}`;
        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
      const data = respBody?.data || respBody;
      setSuccess({
        email:        data?.email || form.email,
        temp_password: data?.temp_password || "",
        user_id:      data?.user_id,
      });
      // Reset the form so the admin can invite another.
      setForm({
        email: "", full_name: "", role: "staff", department: "", designation: "",
        band: "", start_date: "", manager_id: "", work_email: "",
        work_phone: "", employment_type: "",
      });
      onCreated?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copyTempPw = async () => {
    if (!success?.temp_password) return;
    try { await navigator.clipboard.writeText(success.temp_password); setCopied(true); }
    catch { /* clipboard blocked — surface in the box anyway */ }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Email *</label>
          <input className={inputCls} type="email" value={form.email}
            onChange={e => set("email", e.target.value)} placeholder="alice@acme.com" autoComplete="off" />
        </div>
        <div>
          <label className={labelCls}>Full name *</label>
          <input className={inputCls} value={form.full_name}
            onChange={e => set("full_name", e.target.value)} placeholder="Alice Johnson" autoComplete="off" />
        </div>
        <div>
          <label className={labelCls}>Role</label>
          <select className={inputCls} value={form.role} onChange={e => set("role", e.target.value)}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Department</label>
          <input className={inputCls} value={form.department}
            onChange={e => set("department", e.target.value)} placeholder="Engineering" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Job title</label>
          <input className={inputCls} value={form.designation}
            onChange={e => set("designation", e.target.value)} placeholder="Senior Engineer" />
        </div>
        <div>
          <label className={labelCls}>Band</label>
          <select className={inputCls} value={form.band} onChange={e => set("band", e.target.value)}>
            {BANDS.map(b => <option key={b || "none"} value={b}>{b || "—"}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Start date</label>
          <input type="date" className={inputCls} value={form.start_date}
            onChange={e => set("start_date", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Manager</label>
          <select className={inputCls} value={form.manager_id}
            onChange={e => set("manager_id", e.target.value)}>
            <option value="">—</option>
            {managers.map(m => (
              <option key={m.id} value={m.id}>
                {m.full_name || m.email}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Employment type</label>
          <select className={inputCls} value={form.employment_type}
            onChange={e => set("employment_type", e.target.value)}>
            {EMPLOYMENT_TYPES.map(t => <option key={t || "none"} value={t}>{t || "—"}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Work email</label>
          <input className={inputCls} type="email" value={form.work_email}
            onChange={e => set("work_email", e.target.value)} placeholder="alice.work@acme.com" />
        </div>
        <div>
          <label className={labelCls}>Work phone</label>
          <input className={inputCls} value={form.work_phone}
            onChange={e => set("work_phone", e.target.value)} placeholder="+44 …" />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs px-3 py-2">
          {error}
        </div>
      )}

      {success && (
        <div className={`${card} p-4 space-y-2`}>
          <div className="flex items-center gap-2 text-[#00ff90]">
            <CheckCircle size={14} />
            <span className="text-xs uppercase tracking-widest font-medium">User created</span>
          </div>
          <p className="text-sm text-white/70">
            {success.email} can now sign in with this one-time password:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-sm bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-[#00ff90] select-all">
              {success.temp_password}
            </code>
            <button type="button" onClick={copyTempPw}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white">
              <Copy size={12} /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-xs text-yellow-300/60">
            This is the only time the password will be displayed. Share it through a secure channel.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button type="submit" disabled={submitting}
          className="text-xs px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          {submitting ? "Creating…" : "Invite User"}
        </button>
      </div>
    </form>
  );
}
